import type {
  HLWsBook,
  HLWsTrade,
  HLCandle,
  HLAllMids,
  HLActiveAssetCtx,
  HLClearinghouseState,
  HLSubscription,
} from './types';

const WS_URL = 'wss://api.hyperliquid.xyz/ws';
const REST_URL = 'https://api.hyperliquid.xyz/info';
const PING_INTERVAL_MS = 20000;
const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;

type MessageHandler = (data: unknown) => void;

interface SubscriptionEntry {
  subscription: HLSubscription;
  handler: MessageHandler;
}

export class HyperliquidWsClient {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, SubscriptionEntry> = new Map();
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private destroyed = false;

  private getSubKey(sub: HLSubscription): string {
    return `${sub.type}:${sub.coin ?? ''}:${sub.interval ?? ''}`;
  }

  connect(): void {
    if (this.destroyed) return;
    try {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        // Re-subscribe all existing subscriptions
        this.subscriptions.forEach(({ subscription }) => {
          this.sendSubscribe(subscription);
        });
        // Start ping
        this.pingTimer = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ method: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string);
          if (!msg || msg.channel === 'subscriptionResponse' || msg.channel === 'pong') return;
          const channel: string = msg.channel ?? '';
          const data = msg.data;
          // Route to matching subscription handlers
          this.subscriptions.forEach(({ subscription, handler }) => {
            const matches =
              subscription.type === channel ||
              (channel === 'l2Book' && subscription.type === 'l2Book' && data?.coin === subscription.coin) ||
              (channel === 'trades' && subscription.type === 'trades' && Array.isArray(data) && data[0]?.coin === subscription.coin) ||
              (channel === 'candle' && subscription.type === 'candle' && data?.s === subscription.coin && data?.i === subscription.interval) ||
              (channel === 'allMids' && subscription.type === 'allMids') ||
              (channel === 'activeAssetCtx' && subscription.type === 'activeAssetCtx' && data?.coin === subscription.coin);
            if (matches) {
              handler(data);
            }
          });
        } catch {
          // ignore parse errors
        }
      };

      this.ws.onerror = () => {
        // will trigger onclose
      };

      this.ws.onclose = () => {
        this.clearPing();
        if (!this.destroyed) {
          this.scheduleReconnect();
        }
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private sendSubscribe(subscription: HLSubscription): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ method: 'subscribe', subscription }));
    }
  }

  private sendUnsubscribe(subscription: HLSubscription): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ method: 'unsubscribe', subscription }));
    }
  }

  subscribe(subscription: HLSubscription, handler: MessageHandler): () => void {
    const key = this.getSubKey(subscription);
    this.subscriptions.set(key, { subscription, handler });
    this.sendSubscribe(subscription);
    return () => {
      this.subscriptions.delete(key);
      this.sendUnsubscribe(subscription);
    };
  }

  private scheduleReconnect(): void {
    if (this.destroyed || this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
    this.reconnectAttempts++;
    const delay = RECONNECT_DELAY_MS * Math.min(this.reconnectAttempts, 5);
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.clearPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.subscriptions.clear();
  }
}

// REST API helpers
export async function fetchCandleSnapshot(
  coin: string,
  interval: string,
  startTime: number,
  endTime: number
): Promise<HLCandle[]> {
  const res = await fetch(REST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: 'candleSnapshot',
      req: { coin, interval, startTime, endTime },
    }),
  });
  if (!res.ok) throw new Error(`candleSnapshot failed: ${res.status}`);
  return res.json();
}

export async function fetchAllMids(): Promise<Record<string, string>> {
  const res = await fetch(REST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'allMids' }),
  });
  if (!res.ok) throw new Error(`allMids failed: ${res.status}`);
  return res.json();
}

export async function fetchMetaAndAssetCtxs(): Promise<[unknown, HLActiveAssetCtx['ctx'][]]> {
  const res = await fetch(REST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'metaAndAssetCtxs' }),
  });
  if (!res.ok) throw new Error(`metaAndAssetCtxs failed: ${res.status}`);
  return res.json();
}

export async function fetchClearinghouseState(user: string): Promise<HLClearinghouseState> {
  const res = await fetch(REST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'clearinghouseState', user }),
  });
  if (!res.ok) throw new Error(`clearinghouseState failed: ${res.status}`);
  return res.json();
}

export interface HLOpenOrder {
  coin: string;
  side: string; // "B" = buy, "A" = ask/sell
  limitPx: string;
  sz: string;
  oid: number;
  timestamp: number;
  origSz: string;
}

export async function fetchOpenOrders(user: string): Promise<HLOpenOrder[]> {
  const res = await fetch(REST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'openOrders', user }),
  });
  if (!res.ok) throw new Error(`openOrders failed: ${res.status}`);
  return res.json();
}

export async function cancelHyperliquidOrder(params: {
  walletAddress: string;
  assetIndex: number;
  oid: number;
}): Promise<{ success: boolean; message: string }> {
  const ethereum = (window as any).ethereum;
  if (!ethereum) throw new Error('MetaMask not available');

  const nonce = Date.now();

  const cancelAction = {
    type: 'cancel',
    cancels: [{ a: params.assetIndex, o: params.oid }],
  };

  // Compute connectionId: msgpack(action) + 0x00 (no vault) + nonce uint64 BE → keccak256
  const connectionId = await computeConnectionId(cancelAction, nonce, null);

  const domain = {
    name: 'Exchange',
    version: '1',
    chainId: 1337,
    verifyingContract: '0x0000000000000000000000000000000000000000',
  };

  const types = {
    Agent: [
      { name: 'source', type: 'string' },
      { name: 'connectionId', type: 'bytes32' },
    ],
  };

  const agentMessage = { source: 'a', connectionId };

  const sigHex: string = await ethereum.request({
    method: 'eth_signTypedData_v4',
    params: [
      params.walletAddress,
      JSON.stringify({
        domain,
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          ...types,
        },
        primaryType: 'Agent',
        message: agentMessage,
      }),
    ],
  });

  const signature = splitSignature(sigHex);

  const body = {
    action: cancelAction,
    nonce,
    signature,
    vaultAddress: null,
  };

  const res = await fetch('https://api.hyperliquid.xyz/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await res.json();
  if (data?.status === 'ok') {
    return { success: true, message: 'Order cancelled' };
  }
  const errMsg = data?.response?.data?.statuses?.[0] ?? data?.response ?? 'Cancel failed';
  return { success: false, message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg) };
}

// ─── Signing helpers (shared) ─────────────────────────────────────────────────

/**
 * Msgpack-encode a value (minimal implementation for Hyperliquid action objects).
 * Field order in objects matters — caller must pass objects with keys in the correct order.
 */
export function msgpackEncode(value: unknown): Uint8Array {
  const parts: Uint8Array[] = [];

  function encode(v: unknown): void {
    if (v === null || v === undefined) {
      parts.push(new Uint8Array([0xc0]));
    } else if (typeof v === 'boolean') {
      parts.push(new Uint8Array([v ? 0xc3 : 0xc2]));
    } else if (typeof v === 'number') {
      if (Number.isInteger(v) && v >= 0 && v <= 0xffffffff) {
        // uint32
        if (v <= 0x7f) {
          parts.push(new Uint8Array([v]));
        } else if (v <= 0xff) {
          parts.push(new Uint8Array([0xcc, v]));
        } else if (v <= 0xffff) {
          const b = new Uint8Array(3);
          b[0] = 0xcd; b[1] = (v >> 8) & 0xff; b[2] = v & 0xff;
          parts.push(b);
        } else {
          const b = new Uint8Array(5);
          b[0] = 0xce;
          b[1] = (v >>> 24) & 0xff; b[2] = (v >>> 16) & 0xff;
          b[3] = (v >>> 8) & 0xff; b[4] = v & 0xff;
          parts.push(b);
        }
      } else if (Number.isInteger(v) && v < 0) {
        // int32
        const b = new Uint8Array(5);
        b[0] = 0xd2;
        const u = v >>> 0;
        b[1] = (u >>> 24) & 0xff; b[2] = (u >>> 16) & 0xff;
        b[3] = (u >>> 8) & 0xff; b[4] = u & 0xff;
        parts.push(b);
      } else {
        // float64
        const b = new Uint8Array(9);
        b[0] = 0xcb;
        const dv = new DataView(b.buffer);
        dv.setFloat64(1, v, false);
        parts.push(b);
      }
    } else if (typeof v === 'string') {
      const encoded = new TextEncoder().encode(v);
      const len = encoded.length;
      if (len <= 31) {
        parts.push(new Uint8Array([0xa0 | len]));
      } else if (len <= 0xff) {
        parts.push(new Uint8Array([0xd9, len]));
      } else if (len <= 0xffff) {
        parts.push(new Uint8Array([0xda, (len >> 8) & 0xff, len & 0xff]));
      } else {
        parts.push(new Uint8Array([0xdb, (len >>> 24) & 0xff, (len >>> 16) & 0xff, (len >>> 8) & 0xff, len & 0xff]));
      }
      parts.push(encoded);
    } else if (Array.isArray(v)) {
      const len = v.length;
      if (len <= 15) {
        parts.push(new Uint8Array([0x90 | len]));
      } else if (len <= 0xffff) {
        parts.push(new Uint8Array([0xdc, (len >> 8) & 0xff, len & 0xff]));
      } else {
        parts.push(new Uint8Array([0xdd, (len >>> 24) & 0xff, (len >>> 16) & 0xff, (len >>> 8) & 0xff, len & 0xff]));
      }
      for (const item of v) encode(item);
    } else if (typeof v === 'object') {
      const keys = Object.keys(v as object);
      const len = keys.length;
      if (len <= 15) {
        parts.push(new Uint8Array([0x80 | len]));
      } else if (len <= 0xffff) {
        parts.push(new Uint8Array([0xde, (len >> 8) & 0xff, len & 0xff]));
      } else {
        parts.push(new Uint8Array([0xdf, (len >>> 24) & 0xff, (len >>> 16) & 0xff, (len >>> 8) & 0xff, len & 0xff]));
      }
      for (const key of keys) {
        encode(key);
        encode((v as Record<string, unknown>)[key]);
      }
    }
  }

  encode(value);

  // Concatenate all parts
  const totalLen = parts.reduce((sum, p) => sum + p.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const p of parts) {
    result.set(p, offset);
    offset += p.length;
  }
  return result;
}

/** Keccak-256 via browser SubtleCrypto is not available (it's SHA-256 only).
 *  We implement a minimal keccak256 using the ethereum-compatible approach
 *  by calling eth_sign on a known pattern, OR we use a pure-JS implementation.
 *  Here we use a pure-JS keccak256 (tiny implementation).
 */
export async function keccak256Bytes(data: Uint8Array): Promise<string> {
  // Use the browser's SubtleCrypto for SHA-256 as a fallback isn't keccak.
  // We implement keccak256 inline (RC = rotation constants, standard Keccak-256).
  return keccak256Pure(data);
}

function keccak256Pure(data: Uint8Array): string {
  // Keccak-256 pure JS implementation
  const RC: [number, number][] = [
    [0x00000001, 0x00000000],[0x00008082, 0x00000000],[0x0000808a, 0x80000000],
    [0x80008000, 0x80000000],[0x0000808b, 0x00000000],[0x80000001, 0x00000000],
    [0x80008081, 0x80000000],[0x00008009, 0x80000000],[0x0000008a, 0x00000000],
    [0x00000088, 0x00000000],[0x80008009, 0x00000000],[0x8000000a, 0x00000000],
    [0x8000808b, 0x00000000],[0x0000008b, 0x80000000],[0x00008089, 0x80000000],
    [0x00008003, 0x80000000],[0x00008002, 0x80000000],[0x00000080, 0x80000000],
    [0x0000800a, 0x00000000],[0x8000000a, 0x80000000],[0x80008081, 0x80000000],
    [0x00008080, 0x80000000],[0x80000001, 0x00000000],[0x80008008, 0x80000000],
  ];

  const ROTC = [
    1, 3, 6, 10, 15, 21, 28, 36, 45, 55, 2, 14, 27, 41, 56, 8, 25, 43, 62, 18, 39, 61, 20, 44,
  ];

  const PIL = [10, 7, 11, 17, 18, 3, 5, 16, 8, 21, 24, 4, 15, 23, 19, 13, 12, 2, 20, 14, 22, 9, 6, 1];

  function rotl32(x: number, n: number): number {
    return ((x << n) | (x >>> (32 - n))) >>> 0;
  }

  // State: 25 lanes, each lane = [lo, hi] (32-bit each)
  const state: [number, number][] = Array.from({ length: 25 }, () => [0, 0]);

  // Padding: Keccak-256 uses rate=1088 bits = 136 bytes, capacity=512 bits
  const rate = 136;
  const msg = new Uint8Array(data.length + rate);
  msg.set(data);
  msg[data.length] = 0x01;
  msg[msg.length - 1] |= 0x80;

  // Absorb
  for (let block = 0; block < msg.length; block += rate) {
    for (let i = 0; i < rate / 8; i++) {
      const off = block + i * 8;
      const lo = (msg[off] | (msg[off+1] << 8) | (msg[off+2] << 16) | (msg[off+3] << 24)) >>> 0;
      const hi = (msg[off+4] | (msg[off+5] << 8) | (msg[off+6] << 16) | (msg[off+7] << 24)) >>> 0;
      state[i][0] ^= lo;
      state[i][1] ^= hi;
    }
    keccakF(state, RC, ROTC, PIL, rotl32);
  }

  // Squeeze 32 bytes
  const out = new Uint8Array(32);
  for (let i = 0; i < 4; i++) {
    const [lo, hi] = state[i];
    out[i*8+0] = lo & 0xff; out[i*8+1] = (lo >> 8) & 0xff;
    out[i*8+2] = (lo >> 16) & 0xff; out[i*8+3] = (lo >> 24) & 0xff;
    out[i*8+4] = hi & 0xff; out[i*8+5] = (hi >> 8) & 0xff;
    out[i*8+6] = (hi >> 16) & 0xff; out[i*8+7] = (hi >> 24) & 0xff;
  }

  return '0x' + Array.from(out).map(b => b.toString(16).padStart(2, '0')).join('');
}

function keccakF(
  state: [number, number][],
  RC: [number, number][],
  ROTC: number[],
  PIL: number[],
  rotl32: (x: number, n: number) => number
): void {
  const C: [number, number][] = Array.from({ length: 5 }, () => [0, 0]);
  const D: [number, number][] = Array.from({ length: 5 }, () => [0, 0]);
  const B: [number, number][] = Array.from({ length: 25 }, () => [0, 0]);

  for (let round = 0; round < 24; round++) {
    // Theta
    for (let x = 0; x < 5; x++) {
      C[x][0] = state[x][0] ^ state[x+5][0] ^ state[x+10][0] ^ state[x+15][0] ^ state[x+20][0];
      C[x][1] = state[x][1] ^ state[x+5][1] ^ state[x+10][1] ^ state[x+15][1] ^ state[x+20][1];
    }
    for (let x = 0; x < 5; x++) {
      const cx1 = (x + 1) % 5;
      const rotLo = (rotl32(C[cx1][0], 1) ^ (C[cx1][1] >>> 31)) >>> 0;
      const rotHi = (rotl32(C[cx1][1], 1) ^ (C[cx1][0] >>> 31)) >>> 0;
      D[x][0] = (C[(x + 4) % 5][0] ^ rotLo) >>> 0;
      D[x][1] = (C[(x + 4) % 5][1] ^ rotHi) >>> 0;
    }
    for (let i = 0; i < 25; i++) {
      state[i][0] = (state[i][0] ^ D[i % 5][0]) >>> 0;
      state[i][1] = (state[i][1] ^ D[i % 5][1]) >>> 0;
    }

    // Rho + Pi
    B[0] = [state[0][0], state[0][1]];
    for (let i = 0; i < 24; i++) {
      const j = PIL[i];
      const rot = ROTC[i];
      const [lo, hi] = state[j];
      if (rot < 32) {
        B[j] = [
          (rotl32(lo, rot) ^ (hi >>> (32 - rot))) >>> 0,
          (rotl32(hi, rot) ^ (lo >>> (32 - rot))) >>> 0,
        ];
      } else {
        const r = rot - 32;
        B[j] = [
          (rotl32(hi, r) ^ (lo >>> (32 - r))) >>> 0,
          (rotl32(lo, r) ^ (hi >>> (32 - r))) >>> 0,
        ];
      }
    }

    // Chi
    for (let y = 0; y < 5; y++) {
      for (let x = 0; x < 5; x++) {
        let i = y * 5 + x;
        state[i][0] = (B[i][0] ^ ((~B[y*5 + (x+1)%5][0]) & B[y*5 + (x+2)%5][0])) >>> 0;
        state[i][1] = (B[i][1] ^ ((~B[y*5 + (x+1)%5][1]) & B[y*5 + (x+2)%5][1])) >>> 0;
      }
    }

    // Iota
    state[0][0] = (state[0][0] ^ RC[round][0]) >>> 0;
    state[0][1] = (state[0][1] ^ RC[round][1]) >>> 0;
  }
}

/**
 * Compute the connectionId for an L1 action.
 * connectionId = keccak256(msgpack(action) + vaultMarker + nonce_uint64_be)
 */
export async function computeConnectionId(
  action: unknown,
  nonce: number,
  vaultAddress: string | null
): Promise<string> {
  const actionBytes = msgpackEncode(action);

  // Vault marker: 0x01 + 20-byte address, or 0x00
  let vaultBytes: Uint8Array;
  if (vaultAddress) {
    const addrHex = vaultAddress.toLowerCase().replace('0x', '');
    const addrBytes = new Uint8Array(20);
    for (let i = 0; i < 20; i++) {
      addrBytes[i] = parseInt(addrHex.slice(i * 2, i * 2 + 2), 16);
    }
    vaultBytes = new Uint8Array(21);
    vaultBytes[0] = 0x01;
    vaultBytes.set(addrBytes, 1);
  } else {
    vaultBytes = new Uint8Array([0x00]);
  }

  // Nonce as uint64 big-endian (8 bytes)
  const nonceBytes = new Uint8Array(8);
  const nonceBig = BigInt(nonce);
  for (let i = 7; i >= 0; i--) {
    nonceBytes[i] = Number(nonceBig & BigInt(0xff));
    // nonceBig >>= BigInt(8); — avoid mutation
  }
  // Recompute properly
  let tmp = nonceBig;
  for (let i = 7; i >= 0; i--) {
    nonceBytes[i] = Number(tmp & BigInt(0xff));
    tmp >>= BigInt(8);
  }

  const combined = new Uint8Array(actionBytes.length + vaultBytes.length + nonceBytes.length);
  combined.set(actionBytes, 0);
  combined.set(vaultBytes, actionBytes.length);
  combined.set(nonceBytes, actionBytes.length + vaultBytes.length);

  return keccak256Pure(combined);
}

/** Split a 65-byte hex signature into { r, s, v } */
export function splitSignature(sigHex: string): { r: string; s: string; v: number } {
  const sig = sigHex.startsWith('0x') ? sigHex.slice(2) : sigHex;
  const r = '0x' + sig.slice(0, 64);
  const s = '0x' + sig.slice(64, 128);
  let v = parseInt(sig.slice(128, 130), 16);
  if (v < 27) v += 27;
  return { r, s, v };
}

export type { HLWsBook, HLWsTrade, HLCandle, HLAllMids, HLActiveAssetCtx, HLClearinghouseState };
